import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import './Login.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/*
 * AuthMagicLink — verifiziert einen Magic-Link-Token aus der URL
 * (`/auth/magic-link?token=...`) gegen `POST /api/auth/magic-link/verify`.
 *
 * Look & Feel orientiert sich an der bestehenden One. immo Login-Seite.
 */
export default function AuthMagicLink() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const verified = useRef(false);

  // status: 'loading' | 'invalid' | 'forbidden' | 'error'
  const [status, setStatus] = useState('loading');
  const [errorDetail, setErrorDetail] = useState('');

  useEffect(() => {
    // React StrictMode: useEffect feuert zweimal — Token nur einmal verifizieren.
    if (verified.current) return;

    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    verified.current = true;

    // Issue #32: Token sofort aus URL + History entfernen, BEVOR der
    // Verify-Request startet. Wenn fetch() vor der Backend-Konsumierung
    // scheitert (Netzwerk, Deploy-Outage, 5xx vor UPDATE), bleibt das
    // noch gueltige Plain-Token sonst in Adresszeile/History/Screenshots
    // sichtbar. Im Closure haben wir es weiter fuer den POST.
    window.history.replaceState({}, '', '/auth/magic-link');

    fetch(`${API_URL}/auth/magic-link/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          login(data.token);
          navigate('/', { replace: true });
          return;
        }
        if (res.status === 401) {
          setStatus('invalid');
        } else if (res.status === 403) {
          setStatus('forbidden');
        } else {
          setStatus('error');
          setErrorDetail(`HTTP ${res.status}`);
        }
      })
      .catch((err) => {
        console.error('Magic-Link-Verifizierung fehlgeschlagen:', err);
        setStatus('error');
        setErrorDetail(err?.message || 'Netzwerkfehler');
      });
  }, [location.search, login, navigate]);

  return (
    <div className="login2 login2--mounted">
      <div className="login2__bg">
        <div className="login2__gradient" />
        <div className="login2__stars" />
      </div>

      <div className="login2__card-wrap">
        <div className="login2__card">
          <div className="login2__logo">
            <img src="/assets/logos/one-immo-lockup-on-dark.svg" alt="One. immo" />
          </div>

          <div className="login2__divider" />

          {status === 'loading' && (
            <div className="login2__loading">
              <div className="login2__spinner">
                <div className="login2__spinner-building">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <path d="M8 36V14l12-8 12 8v22H8z" stroke="#00a87e" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M16 36v-8h8v8" stroke="#00a87e" strokeWidth="1.5" strokeLinejoin="round" />
                    <rect x="14" y="18" width="4" height="4" rx=".5" fill="#00a87e" opacity=".5" />
                    <rect x="22" y="18" width="4" height="4" rx=".5" fill="#00a87e" opacity=".5" />
                    <rect x="14" y="25" width="4" height="4" rx=".5" fill="#00a87e" opacity=".3" />
                    <rect x="22" y="25" width="4" height="4" rx=".5" fill="#00a87e" opacity=".3" />
                  </svg>
                </div>
                <div className="login2__spinner-ring" />
              </div>
              <p className="login2__loading-text">Magic-Link wird geprüft…</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="login2__body">
              <h1>Link nicht mehr gültig</h1>
              <p>
                Dieser Magic-Link ist abgelaufen oder ungültig. Bitte fordern Sie einen neuen Link an.
              </p>
              <Link to="/login" className="login2__btn" style={{ textDecoration: 'none' }}>
                <span>Zurück zur Anmeldung</span>
              </Link>
            </div>
          )}

          {status === 'forbidden' && (
            <div className="login2__body">
              <h1>Kein Zugang</h1>
              <p>
                Für diese Adresse ist kein Account registriert oder der Zugang ist deaktiviert.
                Bitte wenden Sie sich an Ihre Administratorin oder Ihren Administrator, damit der
                Account freigeschaltet wird.
              </p>
              <Link to="/login" className="login2__btn" style={{ textDecoration: 'none' }}>
                <span>Zurück zur Anmeldung</span>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="login2__body">
              <h1>Etwas ist schiefgegangen</h1>
              <p>
                Der Magic-Link konnte gerade nicht überprüft werden{errorDetail ? ` (${errorDetail})` : ''}.
                Bitte versuchen Sie es gleich noch einmal oder fordern Sie einen neuen Link an.
              </p>
              <Link to="/login" className="login2__btn" style={{ textDecoration: 'none' }}>
                <span>Zurück zur Anmeldung</span>
              </Link>
            </div>
          )}

          <div className="login2__footer">
            One Dot Immo GmbH
          </div>
        </div>
      </div>
    </div>
  );
}
